export interface PatientQueueRowCopyInput {
  activityPreviewText: string | null | undefined;
  displayName: string;
  programText: string | null | undefined;
  requiresAttention: boolean;
  statusText: string | null | undefined;
  summaryText: string | null | undefined;
}

export interface PatientQueueRowCopy {
  metadataText: string;
  nameText: string;
  previewText: string | null;
}

export interface PatientQueueStatusChip {
  key: string;
  labelText: string;
  tone: "accent" | "danger" | "warning";
}

export interface PatientQueueStatusInput {
  hasVoiceCallHandoff: boolean;
  openOutboundCallCount: number;
  pendingApprovalCount: number;
  pendingRedFlagCount: number;
  unreadMessageCount: number;
}

function normalizeCompactText(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/gu, " ");
}

function trimSentence(value: string): string {
  return value.replace(/^[\s·|,-]+/gu, "").replace(/[\s.·|,-]+$/gu, "");
}

function resolveProgramText(value: string | null | undefined): string | null {
  const normalized = normalizeCompactText(value).replace(/^Program:\s*/iu, "");

  if (
    !normalized ||
    /^(?:program\s+)?unassigned$/iu.test(normalized) ||
    /^not assigned$/iu.test(normalized)
  ) {
    return null;
  }

  return normalized;
}

function stripAdminManagedRecordProvenance(value: string): string {
  const marker = " is a patient record managed from the admin console";
  let remainingText = value;
  let markerIndex = remainingText.toLocaleLowerCase().indexOf(marker);

  while (markerIndex >= 0) {
    let sentenceStart = markerIndex;

    while (
      sentenceStart > 0 &&
      !/[.!?]/u.test(remainingText[sentenceStart - 1] ?? "")
    ) {
      sentenceStart -= 1;
    }

    let sentenceEnd = markerIndex + marker.length;

    if (remainingText[sentenceEnd] === ".") {
      sentenceEnd += 1;
    }

    while (/\s/u.test(remainingText[sentenceEnd] ?? "")) {
      sentenceEnd += 1;
    }

    remainingText = `${remainingText.slice(0, sentenceStart)} ${remainingText.slice(sentenceEnd)}`;
    markerIndex = remainingText.toLocaleLowerCase().indexOf(marker);
  }

  return normalizeCompactText(remainingText);
}

function stripQueueBoilerplate(value: string): {
  contactWindowText: string | null;
  programText: string | null;
  remainingText: string;
} {
  let remainingText = stripAdminManagedRecordProvenance(value);
  const programMatch =
    /(?:^|\s)Program:\s*([^.]+)\.?(?=\s|$)/iu.exec(remainingText);
  const contactWindowMatch =
    /(?:^|\s)Contact window:\s*([^.]+)\.?(?=\s|$)/iu.exec(remainingText);

  if (programMatch) {
    remainingText = remainingText.replace(programMatch[0], " ");
  }

  if (contactWindowMatch) {
    remainingText = remainingText.replace(contactWindowMatch[0], " ");
  }

  remainingText = trimSentence(normalizeCompactText(remainingText));

  if (/^Patient record saved from the workbench$/iu.test(remainingText)) {
    remainingText = "Record updated in workbench";
  }

  return {
    contactWindowText: contactWindowMatch
      ? normalizeCompactText(contactWindowMatch[1])
      : null,
    programText: resolveProgramText(programMatch?.[1]),
    remainingText,
  };
}

function pushUnique(parts: string[], value: string | null): void {
  if (
    value &&
    !parts.some((part) => part.toLocaleLowerCase() === value.toLocaleLowerCase())
  ) {
    parts.push(value);
  }
}

export function resolvePatientQueueRowCopy(
  input: PatientQueueRowCopyInput,
): PatientQueueRowCopy {
  const nameText = normalizeCompactText(input.displayName) || "Patient";
  const summaryCopy = stripQueueBoilerplate(input.summaryText ?? "");
  const previewCopy = stripQueueBoilerplate(input.activityPreviewText ?? "");
  const metadataParts: string[] = [];

  pushUnique(
    metadataParts,
    input.requiresAttention
      ? "Needs attention"
      : normalizeCompactText(input.statusText) || null,
  );
  pushUnique(
    metadataParts,
    resolveProgramText(input.programText) ?? summaryCopy.programText,
  );
  pushUnique(
    metadataParts,
    summaryCopy.contactWindowText
      ? `Contact ${summaryCopy.contactWindowText}`
      : null,
  );

  const previewText =
    previewCopy.remainingText || summaryCopy.remainingText || null;

  return {
    metadataText: metadataParts.join(" · "),
    nameText,
    previewText:
      previewText &&
      !metadataParts.some(
        (part) => part.toLocaleLowerCase() === previewText.toLocaleLowerCase(),
      )
        ? previewText
        : null,
  };
}

export function resolvePatientContextSummaryText(input: {
  summaryText: string | null | undefined;
}): string | null {
  const summaryCopy = stripQueueBoilerplate(input.summaryText ?? "");

  return summaryCopy.remainingText || null;
}

export function resolvePatientQueueStatusChips(
  input: PatientQueueStatusInput,
): PatientQueueStatusChip[] {
  return [
    ...(input.openOutboundCallCount > 0
      ? [
          {
            key: "open-calls",
            labelText: `${String(input.openOutboundCallCount)} open call${
              input.openOutboundCallCount === 1 ? "" : "s"
            }`,
            tone: "warning" as const,
          },
        ]
      : []),
    ...(input.hasVoiceCallHandoff
      ? [
          {
            key: "handoff",
            labelText: "Handoff",
            tone: "danger" as const,
          },
        ]
      : []),
    ...(input.pendingRedFlagCount > 0
      ? [
          {
            key: "high-alerts",
            labelText: `${String(input.pendingRedFlagCount)} high alert${
              input.pendingRedFlagCount === 1 ? "" : "s"
            }`,
            tone: "danger" as const,
          },
        ]
      : []),
    ...(input.pendingApprovalCount > 0
      ? [
          {
            key: "reviews",
            labelText: `${String(input.pendingApprovalCount)} review${
              input.pendingApprovalCount === 1 ? "" : "s"
            }`,
            tone: "warning" as const,
          },
        ]
      : []),
    ...(input.unreadMessageCount > 0
      ? [
          {
            key: "unread",
            labelText: `${String(input.unreadMessageCount)} unread`,
            tone: "accent" as const,
          },
        ]
      : []),
  ];
}

export function resolvePatientQueueAccessibilityLabel(input: {
  rowCopy: PatientQueueRowCopy;
  statusChips: PatientQueueStatusChip[];
}): string {
  const statusText = [
    ...input.rowCopy.metadataText.split(" · "),
    ...input.statusChips.map((chip) => chip.labelText),
  ]
    .filter(
      (value, index, values) =>
        value.trim().length > 0 &&
        values.findIndex(
          (candidate) =>
            candidate.toLocaleLowerCase() === value.toLocaleLowerCase(),
        ) === index,
    )
    .join(", ");

  return [
    `Open ${input.rowCopy.nameText}`,
    statusText,
    input.rowCopy.previewText ?? "",
  ]
    .filter((value) => value.trim().length > 0)
    .join(". ");
}
